/**
 * Menu music — low-key ambience for the title screen and menu/UI screens.
 *
 * Owns its own AudioEngine and BeatScheduler so it can run independently of
 * the gameplay audio (which is created/torn down per Game). Stays alive
 * across menu transitions; stops while a level or the editor is active.
 *
 * The first call to start() may happen before any user gesture — in that
 * case the AudioContext stays suspended and the scheduler is not kicked off.
 * The next call from a real gesture handler resumes the context and begins
 * playback.
 */

import { AudioEngine } from './audio-engine';
import { BeatScheduler, type MusicParams } from './beat-scheduler';

const MENU_MUSIC_VOLUME = 0.3;
const MENU_BPM = 80;
const MENU_PARAMS: MusicParams = {
  bpm: MENU_BPM,
  key: 'Am',
  style: 'ambient',
  // Drums and lead are intentionally muted on the menu — they pull too hard.
  layers: ['pad', 'arp'],
  song: 'standby',
};

export class MenuMusic {
  private audio: AudioEngine | null = null;
  private scheduler: BeatScheduler | null = null;
  private playing = false;

  /** Start (or resume) menu music. Safe to call repeatedly and pre-gesture. */
  start(): void {
    if (this.playing) return;

    if (!this.audio) {
      this.audio = new AudioEngine();
      this.audio.musicVolume = MENU_MUSIC_VOLUME;
      this.audio.init();
    } else {
      this.audio.resume();
    }

    // Skip the scheduler if the context is still suspended (no gesture yet).
    // The next call from a real gesture handler will pick up.
    if (this.audio.context?.state !== 'running') return;

    if (!this.scheduler) {
      this.scheduler = new BeatScheduler(this.audio, MENU_PARAMS);
    }
    this.scheduler.start();
    this.playing = true;
  }

  /** Stop menu music. Safe to call when not playing. */
  stop(): void {
    if (!this.playing) return;
    this.scheduler?.stop();
    this.playing = false;
  }
}
